import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { cn } from "@/shared/lib/utils";

type PillVariant = "tinted-purple" | "tinted-teal" | "dark" | "bordered" | "purple";
type PillSize = "sm" | "md" | "lg";

type PillBaseProps = {
  variant?: PillVariant
  size?: PillSize
  icon?: ReactNode
  children: ReactNode
  className?: string
};

type PillAsButton = PillBaseProps & { as: "button" } & ComponentPropsWithoutRef<"button">;

type PillAsSpan = PillBaseProps & { as?: "span" } & ComponentPropsWithoutRef<"span">;

type PillProps = PillAsButton | PillAsSpan;

const VARIANT_CLASSES: Record<PillVariant, string> = {
  "tinted-purple": "bg-[#f4f2ff] text-[#6d28d9]",
  "tinted-teal": "bg-[#e6fbf6] text-[#0d9488]",
  "dark": "bg-[#0a0a0a] text-white hover:bg-[#1a1a1a] active:scale-[0.98] shadow-[0_1px_2px_rgba(0,0,0,0.08),0_8px_24px_rgba(10,10,10,0.12)]",
  "bordered": "bg-white text-[#0a0a0a] border border-[rgba(0,0,0,0.12)] hover:border-[rgba(0,0,0,0.24)] active:scale-[0.98]",
  "purple": "bg-[#8775f9] text-white hover:bg-[#7361e7] active:scale-[0.98] shadow-[0_1px_2px_rgba(0,0,0,0.08),0_8px_24px_rgba(139,116,255,0.30)]",
};

const SIZE_CLASSES: Record<PillSize, string> = {
  sm: "h-7 px-3 text-xs gap-1.5",
  md: "h-9 px-4 text-sm gap-2",
  lg: "h-13 px-7 text-[15px] gap-2.5",
};

const Pill = (props: PillProps) => {
  const {
    variant = "tinted-purple",
    size = "md",
    icon,
    children,
    className,
    ...rest
  } = props;

  const baseClasses = cn(
    "inline-flex items-center justify-center rounded-full font-medium transition-all duration-150 ease-out whitespace-nowrap",
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    className,
  );

  if (props.as === "button") {
    const buttonRest = rest as ComponentPropsWithoutRef<"button">;
    return (
      <button {...buttonRest} className={cn(baseClasses, "cursor-pointer disabled:opacity-60 disabled:pointer-events-none")}>
        {icon}
        {children}
      </button>
    );
  }

  const spanRest = rest as ComponentPropsWithoutRef<"span">;
  return (
    <span {...spanRest} className={baseClasses}>
      {icon}
      {children}
    </span>
  );
};

export default Pill;
